import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';

export const TermsOfServicePage = () => {
  return (
    <div className="bg-[var(--bg-primary)] min-h-screen pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#2a5ff6] transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#2a5ff6]/10 border border-[#2a5ff6]/30 px-4 py-2 mb-6 text-sm text-[#2a5ff6] rounded-full">
            <FileText size={16} />
            Legal
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-white/60">Last updated: February 2, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          <div className="bg-[var(--bg-secondary)] border border-white/10 rounded-2xl p-6 md:p-8 space-y-8">
            
            <section>
              <p className="text-white/80 leading-relaxed">
                These Terms of Service ("Terms") govern your access to and use of <strong className="text-white">Parler Cloud</strong>, including our websites, cloud computing services, content delivery services, security services, and related offerings (collectively, the "Services"), provided by <strong className="text-white">Parler Technologies, Inc.</strong> and its affiliates ("Parler," "we," "our," or "us").
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                By accessing or using the Services, you agree to be bound by these Terms and our <Link to="/privacy" className="text-[#2a5ff6] hover:underline">Privacy Policy</Link>, which is incorporated by reference. If you are using the Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Eligibility and Account Registration</h2>
              <p className="text-white/80 leading-relaxed mb-4">You may use the Services only if:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>You are at least 18 years old</li>
                <li>You are legally permitted to use the Services</li>
                <li>You provide accurate, complete, and current account information</li>
                <li>You are authorized to act on behalf of any entity you represent</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                You are responsible for maintaining the confidentiality of your account credentials and all activity that occurs under your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Services Overview</h2>
              <p className="text-white/80 leading-relaxed">
                Parler Cloud provides infrastructure and related services that allow customers to deploy, operate, and manage workloads. You retain full responsibility for the applications, data, and content you deploy on the Services ("Customer Content").
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                Parler Cloud <strong className="text-white">does not monitor, control, or endorse Customer Content</strong> and is not responsible for its legality, security, or compliance with applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. No SLA Unless Expressly Agreed</h2>
              <p className="text-white/80 leading-relaxed">
                Any references on the website or marketing materials to availability, uptime, performance, or support levels (including statements such as "99.99% uptime" or "24/7 support") are <strong className="text-white">descriptive only</strong> and <strong className="text-white">do not constitute a binding service level agreement</strong>.
              </p>
              <div className="bg-[#2a5ff6]/10 border border-[#2a5ff6]/30 rounded-xl p-4 mt-4">
                <p className="text-white/80 text-sm leading-relaxed">
                  <strong className="text-white">Service Level Agreements (SLAs), service credits, uptime guarantees, response times, or support commitments apply only if expressly set forth in a separate written agreement executed by both parties.</strong>
                </p>
              </div>
              <p className="text-white/80 leading-relaxed mt-4">
                Absent such an agreement, the Services are provided on an <strong className="text-white">as-available basis</strong>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Support Services</h2>
              <p className="text-white/80 leading-relaxed">
                Parler Cloud may offer customer support through designated channels.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                Support availability, response times, and scope <strong className="text-white">may vary</strong> based on plan, service tier, operational conditions, and availability of personnel.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                Parler does not guarantee uninterrupted support availability or resolution timelines unless expressly agreed in writing.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Fees, Pricing, and Taxes</h2>
              <p className="text-white/80 leading-relaxed mb-4">
                You agree to pay all applicable fees associated with your selected Services and plans.
              </p>
              <p className="text-white/80 leading-relaxed mb-4">Unless otherwise stated:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Prices <strong className="text-white">exclude</strong> applicable taxes, regulatory fees, duties, and third-party pass-through costs</li>
                <li>You are responsible for all applicable taxes except those based on Parler's income</li>
                <li>Failure to pay may result in suspension or termination of Services</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                Parler may modify pricing or fees upon reasonable notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Acceptable Use</h2>
              <p className="text-white/80 leading-relaxed mb-4">You agree not to use the Services to:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Violate applicable laws or regulations</li>
                <li>Infringe intellectual property or privacy rights</li>
                <li>Deploy malicious code, malware, or exploit infrastructure</li>
                <li>Interfere with or disrupt the Services or other customers</li>
                <li>Attempt unauthorized access to systems or data</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                Parler may suspend or terminate Services if your use poses security, legal, or operational risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Customer Content and Data</h2>
              <p className="text-white/80 leading-relaxed">
                You retain all rights to Customer Content.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                You grant Parler a limited, non-exclusive license to process Customer Content <strong className="text-white">solely to provide, secure, and support the Services</strong>.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                Parler does not claim ownership over Customer Content.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Security and Responsibility Allocation</h2>
              <p className="text-white/80 leading-relaxed">
                Parler implements reasonable technical and organizational safeguards to protect the Services.
              </p>
              <p className="text-white/80 leading-relaxed mt-4 mb-4">You remain responsible for:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Securing your applications and credentials</li>
                <li>Data protection and backups</li>
                <li>Compliance with applicable laws governing your workloads</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                No system is completely secure, and Parler does not guarantee that security incidents will never occur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Intellectual Property</h2>
              <p className="text-white/80 leading-relaxed">
                The Services, including all underlying technology, software, documentation, and branding, are owned by Parler or its licensors and protected by intellectual property laws.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                You may not copy, modify, reverse engineer, or resell the Services except as expressly permitted.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Suspension and Termination</h2>
              <p className="text-white/80 leading-relaxed mb-4">Parler may suspend or terminate access to the Services:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>For violation of these Terms</li>
                <li>For non-payment</li>
                <li>To address security, legal, or operational risks</li>
                <li>As required by law</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                Upon termination, your right to use the Services ceases immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Disclaimer of Warranties</h2>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-white/80 leading-relaxed uppercase text-sm">
                  THE SERVICES ARE PROVIDED <strong className="text-white">"AS IS" AND "AS AVAILABLE."</strong>
                </p>
                <p className="text-white/80 leading-relaxed uppercase text-sm mt-2">
                  PARLER DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND AVAILABILITY.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Limitation of Liability</h2>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-white/80 leading-relaxed uppercase text-sm mb-2">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                </p>
                <ul className="list-disc list-inside text-white/80 space-y-2 text-sm uppercase">
                  <li>PARLER SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES</li>
                  <li>PARLER'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO PARLER FOR THE SERVICES IN THE SIX (6) MONTHS PRECEDING THE CLAIM</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">13. Indemnification</h2>
              <p className="text-white/80 leading-relaxed mb-4">You agree to indemnify and hold harmless Parler from claims arising from:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Your use of the Services</li>
                <li>Customer Content</li>
                <li>Violation of law or third-party rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">14. Governing Law and Dispute Resolution</h2>
              <p className="text-white/80 leading-relaxed">
                These Terms are governed by the laws of the <strong className="text-white">State of Nevada</strong>, without regard to conflict of law principles.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                Disputes shall be resolved through good-faith negotiation, and if unresolved, through binding arbitration in Nevada, at Parler's discretion.
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                <strong className="text-white">JURY TRIAL WAIVER APPLIES.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">15. Changes to Terms</h2>
              <p className="text-white/80 leading-relaxed">
                Parler may update these Terms from time to time. Continued use of the Services constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">16. Contact Information</h2>
              <div className="p-4 bg-white/5 rounded-xl">
                <p className="text-white font-semibold mb-2">Parler Cloud Legal</p>
                <p className="text-white/80">
                  <strong className="text-white">Email:</strong>{' '}
                  <a href="mailto:legalnotices@parler.com" className="text-[#2a5ff6] hover:underline">legalnotices@parler.com</a>
                </p>
                <p className="text-white/80 mt-2">
                  <strong className="text-white">Address:</strong><br />
                  2801 Network Boulevard, Suite 300<br />
                  Frisco, TX 75034
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="text-[#2a5ff6] hover:underline">Privacy Policy</Link>
            <Link to="/do-not-sell" className="text-[#2a5ff6] hover:underline">Do Not Sell My Information</Link>
            <Link to="/contact" className="text-[#2a5ff6] hover:underline">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
